import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import SectionHead from "../components/SectionHead";
import CTABand from "../components/CTABand";
import PageMeta from "../components/PageMeta";

const APPROACH = [
  { title: "Understand", line: "We understand your clinical and operational requirements." },
  { title: "Plan", line: "We help map the appropriate products, infrastructure and project requirements." },
  { title: "Source", line: "We coordinate procurement from suitable manufacturers and suppliers." },
  { title: "Execute", line: "We support delivery, installation and commissioning." },
  { title: "Support", line: "We continue supporting the facility after implementation." },
];

const About = () => (
  <main data-testid="about-page">
    <PageMeta title="About Us | AccuSurge Medical Solutions" description="AccuSurge Medical Solutions Pvt. Ltd. is an integrated healthcare solutions company — product, project, procurement, support and service for healthcare institutions across India." />
    <section className="relative overflow-hidden bg-brand-950 pb-24 pt-40 md:pb-32 grain" data-testid="about-hero">
      <div className="absolute -right-24 top-0 h-96 w-96 rounded-full bg-brand-600/30 blur-[120px]" aria-hidden="true" />
      <div className="container-x relative">
        <Reveal>
          <span className="eyebrow text-accent">About Us</span>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl md:text-6xl font-semibold tracking-tighter text-white text-balance">
            Healthcare Solutions Built Around Your Needs.
          </h1>
        </Reveal>
      </div>
    </section>

    <section className="py-24 md:py-32">
      <div className="container-x grid gap-16 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <div className="overflow-hidden rounded-2xl">
            <img
              src="https://images.unsplash.com/photo-1758518730037-a16581a040e8?q=80&w=1600&auto=format&fit=crop"
              alt="Healthcare professionals discussing facility requirements"
              loading="lazy"
              className="h-[420px] w-full object-cover"
            />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <span className="eyebrow">Who we are</span>
          <h2 className="mt-5 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-ink">
            An integrated healthcare solutions company.
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-slate-600">
            AccuSurge Medical Solutions Pvt. Ltd. is an integrated healthcare solutions company focused on supporting
            healthcare institutions with products, equipment, project requirements and ongoing services.
          </p>
          <p className="mt-4 text-base md:text-lg leading-relaxed text-slate-600">
            The company combines <strong className="text-ink">Product + Project + Procurement + Support + Service</strong> —
            so hospitals, nursing homes, clinics, dialysis and diagnostic centres can work with one accountable partner
            instead of many vendors.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["Product", "Project", "Procurement", "Support", "Service"].map((w, i) => (
              <span key={w} className="flex items-center gap-2">
                {i > 0 && <span className="text-accent">+</span>}
                <span className="chip !border-brand-200 !bg-brand-50 !text-brand-700">{w}</span>
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-slate-100 py-24 md:py-32">
      <div className="container-x grid gap-6 md:grid-cols-2">
        {[
          { icon: Target, title: "Our Mission", text: "To make healthcare infrastructure and medical equipment more accessible, reliable and efficiently managed for healthcare providers." },
          { icon: Eye, title: "Our Vision", text: "To become a trusted healthcare solutions partner for institutions across India." },
        ].map((m) => (
          <Reveal key={m.title}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-10" data-testid={`about-${m.title.split(" ")[1].toLowerCase()}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-600 text-white">
                <m.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-heading text-2xl font-semibold text-ink">{m.title}</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-600">{m.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="py-24 md:py-32">
      <div className="container-x">
        <SectionHead eyebrow="Our Approach" title="How We Work" lede="Requirement → Planning → Procurement → Execution → Support." />
        <Stagger className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5 md:gap-6">
          {APPROACH.map((a, i) => (
            <StaggerItem key={a.title}>
              <div className="h-full rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-accent/50" data-testid={`approach-${a.title.toLowerCase()}`}>
                <span className="font-mono text-3xl font-medium text-brand-200">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-heading text-xl font-semibold text-ink">{a.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{a.line}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal className="mt-12 text-center md:text-left">
          <Link to="/projects" className="btn-teal" data-testid="about-projects-cta">
            See our project experience <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>

    <CTABand />
  </main>
);

export default About;
